import React, { useCallback, useEffect, useState } from "react";
import { FiTrash2 } from "react-icons/fi";
import { apiRequest, formatMoney } from "../../api";
import { HELP_TYPES } from "../../components/foundation/foundationUtils";

const selectClass = "px-2 py-1 rounded-md bg-navy-dark border border-navy-border text-white text-xs focus:outline-none focus:border-accent-light";
const TYPE_LABEL = Object.fromEntries(HELP_TYPES.map((t) => [t.key, t.label]));

export default function HelpOffersAdmin() {
  const [offers, setOffers] = useState(null);
  const [status, setStatus] = useState("pending");

  const load = useCallback(async () => {
    const { ok, data } = await apiRequest(`/core/foundation/offers/${status ? `?status=${status}` : ""}`, { auth: true });
    setOffers(ok ? data : []);
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const setOfferStatus = async (offer, next) => {
    await apiRequest(`/core/foundation/offers/${offer.id}/`, { method: "PATCH", auth: true, body: { status: next } });
    load();
  };

  const remove = async (offer) => {
    if (!window.confirm("Delete this offer?")) return;
    await apiRequest(`/core/foundation/offers/${offer.id}/`, { method: "DELETE", auth: true });
    load();
  };

  return (
    <div className="w-full">
      <h1 className="text-2xl font-bold mb-2">Help Offers</h1>
      <p className="text-sm text-gray-400 mb-6">
        People who offered to help. Contact them, arrange how the help is delivered, then mark the offer <b>Confirmed</b> once
        you have received it - only confirmed money counts towards a request's "raised" total and appears on its public page.
      </p>

      <div className="flex flex-wrap gap-2 mb-5">
        {[["pending", "Pending"], ["confirmed", "Confirmed"], ["declined", "Declined"], ["", "All"]].map(([key, label]) => (
          <button key={key} onClick={() => setStatus(key)} className={`px-4 py-1.5 rounded-full text-sm ${status === key ? "bg-brand-gradient text-white" : "bg-white/5 border border-navy-border text-gray-300"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="bg-navy border border-navy-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-gray-400 text-left">
            <tr>
              <th className="px-4 py-3">Helper</th>
              <th className="px-4 py-3">For request</th>
              <th className="px-4 py-3">Help</th>
              <th className="px-4 py-3">Message</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {(offers || []).map((o) => (
              <tr key={o.id} className="border-t border-navy-border align-top">
                <td className="px-4 py-3">
                  <p className="text-white font-medium">{o.helper_name}{o.is_anonymous && <span className="ml-2 text-[10px] text-gray-400">(anonymous publicly)</span>}</p>
                  <p className="text-xs text-gray-400">{o.email}{o.email && o.phone && " · "}{o.phone}</p>
                  <p className="text-[11px] text-gray-600">{new Date(o.created_at).toLocaleString()}</p>
                </td>
                <td className="px-4 py-3 text-xs text-gray-300">{o.case_title}</td>
                <td className="px-4 py-3 text-xs text-gray-200">{TYPE_LABEL[o.help_type]}{o.help_type === "money" && <><br /><span className="text-accent-light font-semibold">{formatMoney(o.amount)}</span></>}</td>
                <td className="px-4 py-3 text-xs text-gray-300 max-w-xs whitespace-pre-line">{o.message || "—"}</td>
                <td className="px-4 py-3">
                  <select className={selectClass} value={o.status} onChange={(e) => setOfferStatus(o, e.target.value)}>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed (received)</option>
                    <option value="declined">Declined</option>
                  </select>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => remove(o)} className="p-1.5 rounded-md text-red-400 hover:bg-red-500/10" aria-label="Delete"><FiTrash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {offers && offers.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">No offers here.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
